import _ from 'lodash'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Step, { StepProps } from '@mui/material/Step'
import StepContent from '@mui/material/StepContent'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'

import Availabilities from '@/components/ui/calendar/Availabilities'
import OrderStepCard from '@/components/ui/cards/OrderStepCard'
import TextInput from '@/components/ui/inputs/TextInput'

import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'

import { adapterLocaleMap } from '@/utils/locale'
import { generateSlots } from '@/utils/slots'

import { BookingFormContext, BookingFormValues } from './BookingForm'
import BottomBar from './BottomBar'
import Summary from './Summary'

dayjs.extend(localizedFormat)

export type DateTimeStepProps = StepProps & {
  schedules: Array<{
    address: { description: string; placeId: string } | null
    busySlots: Array<{ start: string; end: string }>
    duration: number
    isRecurrence: boolean
    meetingPlace: AvailabilityMeetingPlace
    recurrence?: Array<{
      days: number[]
      startTime: number
      endTime: number
    }>
    scheduledDay?: Date
    scheduledPeriods?: Array<{ startTime: number; endTime: number }>
    specialities: string[]
  }>
}

export function DateTimeStep(props: DateTimeStepProps) {
  const { active, completed, index = 0, last, schedules: schedulesProp, ...rest } = props
  const { isMobile, onComplete, onEditStep, onNextStep, onPreviousStep } = useContext(BookingFormContext)
  const { setFieldValue, values } = useFormikContext<BookingFormValues>()
  const { t, i18n } = useTranslation()

  const stepProps = {
    active,
    completed,
    index,
    last,
    ...rest,
  }

  const { address, meetingPlace, speciality } = values

  const [currentCalendarViewStartDate, setCurrentCalendarViewStartDate] = useState<Date | null>(isMobile ? null : new Date())
  const [selectedSlot, setSelectedSlot] = useState<string>()

  const schedules = useMemo(
    () =>
      _.filter(
        schedulesProp,
        (schedule) =>
          _.includes(schedule.specialities, speciality) &&
          (schedule.meetingPlace === AvailabilityMeetingPlace.CENTER ? schedule.address?.placeId === address?.placeId : schedule.meetingPlace === meetingPlace)
      ),
    [address, meetingPlace, schedulesProp, speciality]
  )

  const availabilities = useMemo(() => {
    if (!currentCalendarViewStartDate) return []

    return _.range(7).map((dayOfTheWeek) => {
      const day = dayjs(currentCalendarViewStartDate.toISOString()).add(dayOfTheWeek, 'day')
      return {
        date: day.format('DD/MM/YYYY'),
        slots: _.uniqWith(
          _.flatten(
            _.map(schedules, (schedule) => {
              if (schedule.isRecurrence) {
                return generateSlots(day.toDate(), schedule.recurrence || [], schedule.busySlots, schedule.duration)
              }
              if (!schedule.scheduledDay || dayjs(schedule.scheduledDay).format('DD/MM/YYYY') !== day.format('DD/MM/YYYY')) return []

              return generateSlots(
                day.toDate(),
                _.map(schedule.scheduledPeriods, (period) => ({ days: [day.day()], ...period })),
                schedule.busySlots,
                schedule.duration
              )
            })
          ),
          _.isEqual
        ),
      }
    })
  }, [currentCalendarViewStartDate, schedules])

  useEffect(() => {
    setFieldValue('startDate', '')
  }, [setFieldValue, speciality])

  const handleCalendarViewChange = useCallback((fromDate: Date, toDate: Date) => {
    setCurrentCalendarViewStartDate(fromDate)
  }, [])

  const handleSelectSlot = useCallback(
    (startFrom: Date) => {
      setSelectedSlot(startFrom.toISOString())
      setFieldValue('startDate', startFrom.toISOString())
    },
    [setFieldValue]
  )

  if (!active && !completed) {
    return isMobile ? null : (
      <Typography variant="h3" mt={1} mb={1}>
        {t('page.booking.bookingForm.step.dateTime.label')}
      </Typography>
    )
  }

  if (isMobile) {
    return (
      <Step {...stepProps}>
        {completed ? null : (
          <StepContent sx={{ height: '100%' }}>
            <Stack height="100%" spacing={2}>
              <Typography variant="h2" mt={1} mb={1}>
                {t('page.booking.bookingForm.step.dateTime.label')}
              </Typography>
              <Box flex={1}>
                <Paper sx={{ borderRadius: '6px' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocaleMap[i18n.language.split('-')[0]]}>
                    <StaticDatePicker<Date>
                      disablePast
                      displayStaticWrapperAs="desktop"
                      value={currentCalendarViewStartDate}
                      onChange={(newValue) => {
                        if (newValue) setCurrentCalendarViewStartDate(newValue)
                      }}
                      renderInput={(params) => <TextInput {...params} />}
                      shouldDisableDate={(date: Date) => {
                        return !_.some(schedules, (schedule) => {
                          if (schedule.isRecurrence) {
                            return _.uniq(_.flatten(_.map(schedule.recurrence, 'days'))).includes(dayjs(date).day())
                          }

                          return dayjs(schedule.scheduledDay).isSame(date, 'day')
                        })
                      }}
                    />
                  </LocalizationProvider>
                  {currentCalendarViewStartDate && (
                    <Stack pl={2} pr={2} pb={2} spacing={1}>
                      <Typography component="div">{dayjs(currentCalendarViewStartDate).format('ddd, LL')}</Typography>
                      <Box>
                        <Grid container columnSpacing={3} rowSpacing={1}>
                          {_.map(
                            _.find(availabilities, (availability) => dayjs(currentCalendarViewStartDate).isSame(dayjs(availability.date, 'DD/MM/YYYY'), 'day'))
                              ?.slots,
                            (slot) => (
                              <Grid key={`${slot} - ${index}`} item xs={4}>
                                <Chip
                                  color="primary"
                                  sx={[
                                    { width: '100%', height: 35 },
                                    selectedSlot === slot.start && {
                                      backgroundColor: (theme) => `${theme.palette.primary.main} !important`,
                                      color: 'white',
                                    },
                                  ]}
                                  label={dayjs(slot.start).isValid() ? dayjs(slot.start).format('HH[h]mm') : ''}
                                  onClick={() => {
                                    handleSelectSlot(dayjs(slot.start).toDate())
                                  }}
                                />
                              </Grid>
                            )
                          )}
                        </Grid>
                      </Box>
                    </Stack>
                  )}
                </Paper>
              </Box>
              <BottomBar
                disabled={!values.startDate}
                isFirst={index === 0}
                isLast={last}
                onComplete={onComplete}
                onNext={onNextStep}
                onPrevious={onPreviousStep}
              />
            </Stack>
          </StepContent>
        )}
      </Step>
    )
  }

  return (
    <Step {...stepProps}>
      {completed ? (
        <StepLabel icon={<EventAvailableRoundedIcon />}>
          <Summary
            label={t('page.booking.bookingForm.step.dateTime.label')}
            onEdit={() => onEditStep(index)}
            text={dayjs(values.startDate).isValid() ? dayjs(values.startDate).format('DD/MM/YYYY HH[h]mm') : ''}
          />
        </StepLabel>
      ) : (
        <StepContent>
          <OrderStepCard
            disabled={!values.startDate}
            isFirst={index === 0}
            isLast={last}
            title={t('page.booking.bookingForm.step.dateTime.label')}
            onComplete={onComplete}
            onNext={onNextStep}
            onPrevious={onPreviousStep}
          >
            <Box sx={{ p: '10px 30px 35px' }}>
              <Availabilities
                availabilities={availabilities}
                onNext={handleCalendarViewChange}
                onPrevious={handleCalendarViewChange}
                onSelectSlot={handleSelectSlot}
                selectedSlot={selectedSlot}
                startFrom={currentCalendarViewStartDate || undefined}
              />
            </Box>
          </OrderStepCard>
        </StepContent>
      )}
    </Step>
  )
}

export default DateTimeStep
